/* Copyright (c) 2020-present CloudIO Inc */

import { baseColumnProps, isBaseColumnPropertyVisible } from '../BaseColumn';
import { Platform } from '../../../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import { defaultValue } from './RowSelectionColumn';

const RowSelectionColumnManager: ComponentManagerFn<'RowSelectionColumn'> = (
  platform: Platform,
): ComponentManager<'RowSelectionColumn'> => {
  return {
    ...baseComponentManager<'RowSelectionColumn'>(
      platform,
      'RowSelectionColumn',
    ),
    type: 'RowSelectionColumn',
    name: 'Row Selection Column',
    isChildAllowed: (page, item, childType) =>
      (['RowAction'] as ItemType[]).includes(childType as ItemType),
    defaultValue,
    props: {
      disableCondition: {
        label: 'Disable Condition',
        type: 'expressionOnly',
        returnType: () => 'boolean',
        helpText: 'Provide expression value to disable the selection.',
      },
      ...baseColumnProps,
      viewAttribute: {
        ...baseColumnProps.viewAttribute,
        required: false,
      },
    },
    isPropertyVisible(prop, itemData, { appUid, itemId, pageId }): boolean {
      if (
        itemId &&
        !isBaseColumnPropertyVisible({ appUid, platform, itemId, pageId, prop })
      ) {
        return false;
      }
      switch (prop) {
        case 'viewAttribute':
        case 'insertable':
        case 'updatable':
        case 'filterable':
        case 'sortable':
        case 'alignment':
        case 'label':
        case 'flex':
        case 'pinRight':
        case 'required':
          return false;
        default:
          return true;
      }
    },
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    category: 'Data Grid',
    icon: { iconName: 'io-row-selection', iconType: 'cloudio' },
    description: `**Overview**<br/>
The Row Selection Column is a control column that adds a checkbox to every row in your Data Grid.
It enables Multi-Row Selection, allowing users to check specific boxes (or use the "Select All" checkbox in the header) to pick records for bulk processing. It is the essential foundation for workflows like "Delete Selected," "Approve All," or "Export Selected to Excel."

**1. Integration & Actions**<br/>
While the column handles the selection (the checkboxes), it also serves as the container for the operations available on those selections.
* **Child Component (Row Action):** To define what happens when rows are selected, you must add **Row Action** components as children of this column.
* **Behavior:** Actions added here (e.g., "Archive Selected") typically appear in the grid's toolbar or context menu, becoming active only when the user checks one or more boxes.
**2. Constraints & Logic**<br/>
You may not want every row to be selectable. This column allows you to lock specific records based on their data.
* **Disable Condition:** A boolean expression that evaluates for each row.
* **Logic:** If the expression returns true, the checkbox for that row is disabled (greyed out), and the user cannot select it.
* **Example:** \`$.status === 'Locked'\` or \`$.role === 'Admin'\`. Use this to prevent users from accidentally deleting or modifying protected records during a bulk select.

**3. Configuration Behavior**<br/>
This column is purely functional and has stripped-down configuration compared to data columns.
* **No Data Binding:** It does not bind to a View Attribute because the "Selection State" is transient (memory-only) and managed by the grid, not stored in your database.
* **Fixed Position:** By convention (and often enforced by the platform), this column is usually pinned to the far left, next to the Row Number column.
* **No Sorting/Filtering:** You cannot sort or filter based on whether a row is "checked."`,
  };
};

export default RowSelectionColumnManager;
